import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Home,
  Wallet as WalletIcon,
  Sparkles,
  RotateCcw,
  Volume2,
  Play,
  Pause,
  Target,
  RefreshCw,
  Check,
  X,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import LanguageSelector from "../components/LanguageSelector";

/* --- slideshow --------------------------------------------------------- */
const SLIDES = [
  "/mental-math/apply_math_1.png",
  "/mental-math/apply_math_2.png",
  "/mental-math/apply_math_3.png",
];

function Slideshow() {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setI((n) => (n + 1) % SLIDES.length), 3500);
    return () => clearInterval(id);
  }, [playing]);
  return (
    <div
      className="relative w-full aspect-square rounded-3xl overflow-hidden border-4 border-white bg-slate-100 shadow-[0_8px_0_rgba(0,0,0,0.12)]"
      data-testid="mental-math-slideshow"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={SLIDES[i]}
          src={SLIDES[i]}
          alt="mental math practice"
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.55 }}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </AnimatePresence>
      {/* play/pause toggle */}
      <button
        type="button"
        data-testid="mm-slideshow-toggle"
        onClick={() => setPlaying((p) => !p)}
        aria-label={playing ? "Pause slideshow" : "Play slideshow"}
        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-slate-800 border-2 border-white shadow-[0_2px_6px_rgba(0,0,0,0.25)] flex items-center justify-center backdrop-blur-sm active:scale-95 transition-transform"
      >
        {playing ? (
          <Pause size={18} strokeWidth={3} />
        ) : (
          <Play size={18} strokeWidth={3} className="translate-x-[1px]" />
        )}
      </button>
      {/* progress dots */}
      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
        {SLIDES.map((_, k) => (
          <span
            key={k}
            className={`h-1.5 rounded-full transition-all ${
              k === i ? "w-6 bg-white" : "w-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* --- concentration / target animation --------------------------------- */
function FocusTarget({ subtitle }) {
  return (
    <div
      className="w-full aspect-[4/3] rounded-3xl border-4 border-white bg-gradient-to-br from-indigo-500 via-violet-600 to-fuchsia-600 shadow-[0_8px_0_rgba(0,0,0,0.14)] flex flex-col items-center justify-center gap-4 text-center px-6"
      data-testid="mental-math-focus"
    >
      {/* pulsing rings */}
      <div className="relative w-40 h-40 flex items-center justify-center">
        {[0, 1, 2].map((k) => (
          <motion.span
            key={k}
            className="absolute inset-0 rounded-full border-4 border-white/60"
            initial={{ scale: 0.4, opacity: 0.9 }}
            animate={{ scale: 1.15, opacity: 0 }}
            transition={{
              duration: 2.2,
              delay: k * 0.7,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        ))}
        <motion.div
          className="w-24 h-24 rounded-full bg-white/95 flex items-center justify-center shadow-inner"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Target size={54} strokeWidth={2.5} className="text-fuchsia-600" />
        </motion.div>
      </div>
      <p className="font-heading text-white text-lg font-bold tracking-wide drop-shadow">
        {subtitle}
      </p>
    </div>
  );
}

/* --- tiny beeps for feedback ------------------------------------------ */
function playBeep(kind) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    if (kind === "correct") {
      // Two ascending short beeps.
      o.type = "sine";
      o.frequency.setValueAtTime(660, ctx.currentTime);
      o.frequency.setValueAtTime(990, ctx.currentTime + 0.14);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start();
      o.stop(ctx.currentTime + 0.36);
    } else {
      // Low buzz.
      o.type = "sawtooth";
      o.frequency.setValueAtTime(180, ctx.currentTime);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
      o.start();
      o.stop(ctx.currentTime + 0.3);
    }
  } catch (e) {
    /* ignore audio failures — visual feedback still works */
  }
}

/* --- utility: stable shuffle for options ------------------------------ */
function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ---------------------------------------------------------------------- */
export default function MentalMath() {
  const navigate = useNavigate();
  const { user, lang, config, updateBalance, logout } = useAuth();
  const currency = config?.currency_symbol || "₹";
  const cost = config?.mental_math_price ?? 3;
  const balance = user?.wallet_balance ?? 0;

  const [phase, setPhase] = useState("intro"); // intro | focus | question | error
  const [data, setData] = useState(null); // { question, correct_answer, wrong_answers, audio_b64, audio_mime, audio_error }
  const [selected, setSelected] = useState(null); // number
  const [locked, setLocked] = useState(false); // lock after correct answer
  const [errorMsg, setErrorMsg] = useState("");

  const audioRef = useRef(null);

  // Options are shuffled once per question.
  const options = useMemo(() => {
    if (!data) return [];
    return shuffled([data.correct_answer, ...(data.wrong_answers || [])]);
  }, [data]);

  const audioSrc = useMemo(() => {
    if (!data?.audio_b64) return null;
    return `data:${data.audio_mime || "audio/wav"};base64,${data.audio_b64}`;
  }, [data]);

  const startPractice = async () => {
    if (balance < cost) {
      toast.error(
        t(lang, "insufficient_wallet") ||
          `Insufficient wallet balance. Need ${currency}${cost}.`
      );
      navigate("/wallet");
      return;
    }
    setPhase("focus");
    setSelected(null);
    setLocked(false);
    setData(null);
    setErrorMsg("");
    try {
      const form = new URLSearchParams();
      form.append("language", lang || "en");
      const r = await api.post("/generate/mental-math", form, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (typeof r.data.wallet_balance === "number") updateBalance(r.data.wallet_balance);
      setData(r.data);
      setPhase("question");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(String(msg));
      setPhase("error");
      toast.error(msg);
    }
  };

  // Auto-play the question audio the moment we land on the question phase.
  useEffect(() => {
    if (phase !== "question" || !audioSrc) return;
    const a = audioRef.current;
    if (!a) return;
    a.play().catch(() => { /* browsers may block until user interaction */ });
  }, [phase, audioSrc]);

  const replayAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => { /* ignore */ });
  };

  const selectOption = (value) => {
    if (locked) return;
    setSelected(value);
    const isCorrect = Number(value) === Number(data.correct_answer);
    if (isCorrect) {
      setLocked(true);
      playBeep("correct");
      toast.success(t(lang, "mm_correct"));
    } else {
      playBeep("wrong");
      toast.error(t(lang, "mm_wrong"));
    }
  };

  return (
    <div className="page-shell" data-testid="mental-math-page">
      {/* Header ------------------------------------------------------- */}
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="mm-back-btn"
          onClick={() => navigate("/math")}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          aria-label={t(lang, "mm_back")}
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <button
          data-testid="mm-wallet-pill"
          onClick={() => navigate("/wallet")}
          className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-500 text-white px-4 py-2 rounded-full font-bold shadow-[0_4px_0_rgba(0,0,0,0.1)] border-2 border-emerald-600/20 active:translate-y-1 active:shadow-none transition-all"
        >
          <WalletIcon size={18} strokeWidth={2.5} />
          {currency}<span data-testid="mm-wallet-balance">{balance}</span>
        </button>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <button
            data-testid="mm-home-btn"
            onClick={() => navigate("/")}
            className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
            aria-label={t(lang, "mm_home")}
          >
            <Home size={18} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>
      </div>

      {/* Title ------------------------------------------------------- */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 text-center"
      >
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={16} strokeWidth={2.5} className="text-violet-500" />
          {t(lang, "math_mental")}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-3 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "math_mental")}
        </h1>
        <p className="text-sm sm:text-base text-cyan-50/95 mt-1 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "mm_intro")}
        </p>
      </motion.div>

      {/* Main body --------------------------------------------------- */}
      <div className="mt-6">
        {phase === "intro" && (
          <>
            <Slideshow />
            <button
              data-testid="mm-start-practice-btn"
              onClick={startPractice}
              className="mt-6 w-full bg-pink-400 hover:bg-pink-500 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2 shadow-[0_4px_18px_-4px_rgba(15,23,42,0.35)]"
            >
              <Play size={22} strokeWidth={3} /> {t(lang, "mm_start_practice")}
            </button>
          </>
        )}

        {phase === "focus" && (
          <>
            <FocusTarget subtitle={t(lang, "mm_generating")} />
            <p className="mt-4 text-center text-sm text-cyan-50 font-semibold drop-shadow">
              {t(lang, "mm_focus_now")}
            </p>
          </>
        )}

        {phase === "question" && data && (
          <div data-testid="mm-question-panel">
            <FocusTarget subtitle={t(lang, "mm_focus_now")} />

            {/* audio (hidden) + replay button */}
            {audioSrc && (
              <audio
                ref={audioRef}
                src={audioSrc}
                preload="auto"
                data-testid="mm-audio"
                className="hidden"
              />
            )}
            <div className="mt-4 flex items-center justify-center">
              <button
                data-testid="mm-replay-audio-btn"
                onClick={replayAudio}
                disabled={!audioSrc}
                className="inline-flex items-center gap-2 bg-white text-slate-800 font-heading font-bold py-2.5 px-5 rounded-full border-2 border-slate-900/10 btn-squish disabled:opacity-50"
              >
                <Volume2 size={18} strokeWidth={2.5} className="text-sky-500" />
                {t(lang, "mm_replay_audio")}
              </button>
            </div>
            {data.audio_error && !audioSrc && (
              <p className="mt-2 text-center text-xs text-rose-100 font-semibold">
                {data.audio_error}
              </p>
            )}

            {/* answers */}
            <p className="mt-6 text-center text-white font-heading text-lg font-bold drop-shadow">
              {t(lang, "mm_choose_answer")}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {options.map((val) => {
                const isSel = selected === val;
                const isCorrect = Number(val) === Number(data.correct_answer);
                let cls =
                  "bg-white text-slate-800 border-2 border-slate-900/10";
                let RightIcon = null;
                if (isSel && isCorrect) {
                  cls =
                    "bg-emerald-400 text-white border-2 border-emerald-600/40";
                  RightIcon = Check;
                } else if (isSel && !isCorrect) {
                  cls = "bg-rose-400 text-white border-2 border-rose-600/40";
                  RightIcon = X;
                }
                return (
                  <button
                    key={val}
                    data-testid={`mm-option-${val}`}
                    disabled={locked}
                    onClick={() => selectOption(val)}
                    className={`w-full py-4 px-5 rounded-2xl font-heading text-2xl font-bold btn-squish flex items-center justify-between shadow-[0_4px_0_rgba(0,0,0,0.1)] ${cls} disabled:opacity-90 disabled:active:translate-y-0`}
                  >
                    <span className="tabular-nums">{val}</span>
                    {RightIcon && <RightIcon size={26} strokeWidth={3} />}
                  </button>
                );
              })}
            </div>

            {/* nav */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                data-testid="mm-practice-another-btn"
                onClick={startPractice}
                className="bg-sky-400 hover:bg-sky-500 text-white font-heading font-bold uppercase py-3.5 rounded-full btn-squish inline-flex items-center justify-center gap-2 leading-none"
              >
                <RefreshCw size={18} strokeWidth={3} className="shrink-0" />
                <span>{t(lang, "mm_practice_another")}</span>
              </button>
              <button
                data-testid="mm-home-cta"
                onClick={() => navigate("/")}
                className="bg-white text-slate-700 font-heading font-bold uppercase py-3.5 rounded-full border-2 border-slate-900/10 btn-squish inline-flex items-center justify-center gap-2 leading-none"
              >
                <Home size={18} strokeWidth={3} className="shrink-0" />
                <span>{t(lang, "mm_home")}</span>
              </button>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="mt-4 bg-white rounded-3xl p-6 border-4 border-rose-300 text-center" data-testid="mm-error-panel">
            <p className="font-heading text-lg font-bold text-rose-500">
              {errorMsg || "Something went wrong."}
            </p>
            <button
              data-testid="mm-retry-btn"
              onClick={startPractice}
              className="mt-4 inline-flex items-center gap-2 bg-pink-400 hover:bg-pink-500 text-white font-heading font-bold uppercase py-3 px-6 rounded-full btn-squish"
            >
              <RotateCcw size={18} strokeWidth={3} /> {t(lang, "mm_start_practice")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
